import { performanceStore } from "./store";

interface PreviousSample {
  at: number;
  bytesReceived: number;
  framesDecoded: number;
}

export class WebRtcStatsCollector {
  private timer?: number;
  private previous?: PreviousSample;

  constructor(
    private readonly peer: RTCPeerConnection,
    private readonly inputChannel: () => RTCDataChannel | undefined,
  ) {}

  start() {
    this.stop();
    void this.collect();
    this.timer = window.setInterval(() => void this.collect(), 1000);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }

  private async collect() {
    const report = await this.peer.getStats();
    const records = new Map<string, any>();
    report.forEach((value) => records.set(value.id, value));
    const inbound = [...records.values()].find((value) =>
      value.type === "inbound-rtp" && value.kind === "video" && !value.isRemote);
    const pair = [...records.values()].find((value) =>
      value.type === "candidate-pair" && value.state === "succeeded" && (value.selected || value.nominated));
    const channelStats = [...records.values()].find((value) =>
      value.type === "data-channel" && value.label === "input");
    const channel = this.inputChannel();
    const now = performance.now();

    if (inbound) {
      const codec = records.get(inbound.codecId);
      const elapsed = this.previous ? Math.max(1, now - this.previous.at) : 1000;
      const bitrateMbps = this.previous
        ? ((inbound.bytesReceived - this.previous.bytesReceived) * 8) / elapsed / 1000
        : undefined;
      const measuredFps = this.previous
        ? ((inbound.framesDecoded - this.previous.framesDecoded) * 1000) / elapsed
        : inbound.framesPerSecond;
      const totalFrames = (inbound.framesDecoded || 0) + (inbound.framesDropped || 0);
      performanceStore.update({
        connectionState: this.peer.connectionState,
        codec: codec?.mimeType,
        decoderImplementation: inbound.decoderImplementation,
        powerEfficientDecoder: inbound.powerEfficientDecoder,
        width: inbound.frameWidth,
        height: inbound.frameHeight,
        fps: Math.round((inbound.framesPerSecond ?? measuredFps ?? 0) * 10) / 10,
        framesReceived: inbound.framesReceived,
        framesDecoded: inbound.framesDecoded,
        framesDropped: inbound.framesDropped,
        droppedFramePercent: totalFrames
          ? Math.round(((inbound.framesDropped || 0) / totalFrames) * 10_000) / 100
          : 0,
        keyFramesDecoded: inbound.keyFramesDecoded,
        totalDecodeTimeMs: Math.round((inbound.totalDecodeTime || 0) * 1000 * 10) / 10,
        averageDecodeTimeMs: inbound.framesDecoded
          ? Math.round((inbound.totalDecodeTime / inbound.framesDecoded) * 1000 * 100) / 100
          : 0,
        jitterMs: Math.round((inbound.jitter || 0) * 1000 * 10) / 10,
        jitterBufferDelayMs: Math.round((inbound.jitterBufferDelay || 0) * 1000 * 10) / 10,
        averageJitterBufferResidenceMs: inbound.jitterBufferEmittedCount
          ? Math.round((inbound.jitterBufferDelay / inbound.jitterBufferEmittedCount) * 1000 * 10) / 10
          : 0,
        freezeCount: inbound.freezeCount,
        totalFreezeDurationMs: Math.round((inbound.totalFreezesDuration || 0) * 1000),
        packetsLost: inbound.packetsLost,
        bytesReceived: inbound.bytesReceived,
        bitrateMbps: bitrateMbps === undefined ? undefined : Math.round(bitrateMbps * 100) / 100,
      });
      this.previous = { at: now, bytesReceived: inbound.bytesReceived, framesDecoded: inbound.framesDecoded };
    }

    if (pair) {
      const local = records.get(pair.localCandidateId);
      const remote = records.get(pair.remoteCandidateId);
      const relayProtocol = local?.relayProtocol || remote?.relayProtocol;
      performanceStore.update({
        localCandidateType: local?.candidateType,
        remoteCandidateType: remote?.candidateType,
        protocol: pair.protocol || local?.protocol || remote?.protocol,
        relayProtocol,
        rttMs: pair.currentRoundTripTime === undefined
          ? undefined : Math.round(pair.currentRoundTripTime * 1000 * 10) / 10,
        availableIncomingBitrateMbps: pair.availableIncomingBitrate === undefined
          ? undefined : Math.round((pair.availableIncomingBitrate / 1_000_000) * 100) / 100,
        icePath: `${local?.candidateType || "?"} → ${remote?.candidateType || "?"} · ${pair.protocol || local?.protocol || "?"}${relayProtocol ? `/${relayProtocol}` : ""}`,
      });
    }

    performanceStore.update({
      dataChannelState: channel?.readyState,
      dataBufferedAmount: channel?.bufferedAmount,
      dataMessagesSent: channelStats?.messagesSent,
      dataMessagesReceived: channelStats?.messagesReceived,
      dataBytesSent: channelStats?.bytesSent,
      dataBytesReceived: channelStats?.bytesReceived,
    });
  }
}
